import React, { useState } from 'react';
import { FileUpload } from '../../FileUpload';
import { ChevronDownIcon, ChevronUpIcon, AlertCircleIcon, CheckIcon } from 'lucide-react';
export function StatementUpload({
  onBack,
  onUploadComplete,
  locale = 'en-US'
}) {
  const [currentStep, setCurrentStep] = useState('upload'); // upload, mapping, validation
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [mappedData, setMappedData] = useState({
    vendor: '',
    vendorId: '',
    period: '',
    statements: []
  });
  const [columnMapping, setColumnMapping] = useState({
    invoiceNumber: '',
    invoiceDate: '',
    amount: '',
    description: ''
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationPassed, setValidationPassed] = useState(false);
  // Sample data for preview
  const [previewData, setPreviewData] = useState([{
    col1: 'INV-123',
    col2: '2023-01-15',
    col3: '1250.75',
    col4: 'Office Supplies'
  }, {
    col1: 'INV-124',
    col2: '2023-01-22',
    col3: '2340.00',
    col4: 'IT Equipment'
  }, {
    col1: 'INV-125',
    col2: '2023-01-30',
    col3: '450.25',
    col4: 'Maintenance'
  }]);
  const handleFileUpload = files => {
    setUploadedFiles(files);
    // In a real app, you would parse the file content here
    // For now, we'll just move to the mapping step
    setCurrentStep('mapping');
  };
  const handleMappingChange = (field, value) => {
    setColumnMapping({
      ...columnMapping,
      [field]: value
    });
  };
  const handleVendorInfoChange = (field, value) => {
    setMappedData({
      ...mappedData,
      [field]: value
    });
  };
  const validateMapping = () => {
    const errors = [];
    if (!mappedData.vendor) {
      errors.push('Vendor name is required');
    }
    if (!mappedData.period) {
      errors.push('Statement period is required');
    }
    if (!columnMapping.invoiceNumber) {
      errors.push('Invoice Number mapping is required');
    }
    if (!columnMapping.amount) {
      errors.push('Amount mapping is required');
    }
    setValidationErrors(errors);
    if (errors.length === 0) {
      // In a real app, you would transform the data according to the mapping here
      // For this demo, we'll create some sample mapped statements
      const sampleMappedStatements = previewData.map((row, index) => ({
        id: `STMT-${index + 1}`,
        invoiceNumber: row[columnMapping.invoiceNumber] || row.col1,
        invoiceDate: row[columnMapping.invoiceDate] || row.col2,
        amount: parseFloat(row[columnMapping.amount] || row.col3),
        description: row[columnMapping.description] || row.col4,
        status: 'Unmatched'
      }));
      setMappedData({
        ...mappedData,
        statements: sampleMappedStatements
      });
      setValidationPassed(true);
    }
    setCurrentStep('validation');
  };
  const handleComplete = () => {
    onUploadComplete(mappedData);
  };
  return <div data-id="element-1798">
      <div className="mb-6" data-id="element-1799">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-1800">
          Upload Vendor Statement
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-1801">
          Upload a vendor statement file and map the columns to reconcile with
          AP invoices
        </p>
      </div>
      {/* Progress Steps */}
      <div className="mb-8" data-id="element-1802">
        <nav aria-label="Progress" data-id="element-1803">
          <ol className="space-y-4 md:flex md:space-y-0 md:space-x-8" data-id="element-1804">
            <li className="md:flex-1" data-id="element-1805">
              <button className={`group pl-4 py-2 flex flex-col border-l-4 md:pl-0 md:pt-4 md:pb-0 md:border-l-0 md:border-t-4 ${currentStep === 'upload' ? 'border-indigo-600' : 'border-gray-200'}`} data-id="element-1806">
                <span className={`text-xs font-semibold tracking-wide uppercase ${currentStep === 'upload' ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-1807">
                  Step 1
                </span>
                <span className="text-sm font-medium" data-id="element-1808">Upload Statement</span>
              </button>
            </li>
            <li className="md:flex-1" data-id="element-1809">
              <button className={`group pl-4 py-2 flex flex-col border-l-4 md:pl-0 md:pt-4 md:pb-0 md:border-l-0 md:border-t-4 ${currentStep === 'mapping' ? 'border-indigo-600' : 'border-gray-200'}`} data-id="element-1810">
                <span className={`text-xs font-semibold tracking-wide uppercase ${currentStep === 'mapping' ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-1811">
                  Step 2
                </span>
                <span className="text-sm font-medium" data-id="element-1812">Map Columns</span>
              </button>
            </li>
            <li className="md:flex-1" data-id="element-1813">
              <button className={`group pl-4 py-2 flex flex-col border-l-4 md:pl-0 md:pt-4 md:pb-0 md:border-l-0 md:border-t-4 ${currentStep === 'validation' ? 'border-indigo-600' : 'border-gray-200'}`} data-id="element-1814">
                <span className={`text-xs font-semibold tracking-wide uppercase ${currentStep === 'validation' ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-1815">
                  Step 3
                </span>
                <span className="text-sm font-medium" data-id="element-1816">Validate</span>
              </button>
            </li>
          </ol>
        </nav>
      </div>
      {/* Upload Step */}
      {currentStep === 'upload' && <div className="space-y-6" data-id="element-1817">
          <FileUpload onFileUpload={handleFileUpload} documentTypes={['Vendor Statement', 'AP Statement', 'Account Statement']} defaultDocumentType="Vendor Statement" data-id="element-1818" />
          <div className="flex justify-end space-x-3" data-id="element-1819">
            <button type="button" onClick={onBack} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1820">
              Cancel
            </button>
          </div>
        </div>}
      {/* Mapping Step */}
      {currentStep === 'mapping' && <div className="space-y-6" data-id="element-1821">
          <div className="bg-white shadow sm:rounded-md" data-id="element-1822">
            <div className="px-4 py-5 sm:p-6" data-id="element-1823">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-1824">
                Statement Information
              </h4>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-1825">
                <div className="sm:col-span-3" data-id="element-1826">
                  <label htmlFor="vendor-name" className="block text-sm font-medium text-gray-700" data-id="element-1827">
                    Vendor Name
                  </label>
                  <input type="text" name="vendor-name" id="vendor-name" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={mappedData.vendor} onChange={e => handleVendorInfoChange('vendor', e.target.value)} data-id="element-1828" />
                </div>
                <div className="sm:col-span-3" data-id="element-1829">
                  <label htmlFor="vendor-id" className="block text-sm font-medium text-gray-700" data-id="element-1830">
                    Vendor ID
                  </label>
                  <input type="text" name="vendor-id" id="vendor-id" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={mappedData.vendorId} onChange={e => handleVendorInfoChange('vendorId', e.target.value)} data-id="element-1831" />
                </div>
                <div className="sm:col-span-3" data-id="element-1832">
                  <label htmlFor="statement-period" className="block text-sm font-medium text-gray-700" data-id="element-1833">
                    Statement Period
                  </label>
                  <input type="text" name="statement-period" id="statement-period" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="e.g. Jan 2023" value={mappedData.period} onChange={e => handleVendorInfoChange('period', e.target.value)} data-id="element-1834" />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white shadow sm:rounded-md" data-id="element-1835">
            <div className="px-4 py-5 sm:p-6" data-id="element-1836">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-1837">
                Column Mapping
              </h4>
              <p className="text-sm text-gray-500 mb-4" data-id="element-1838">
                Map the columns from your statement file to the corresponding
                fields in our system.
              </p>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-1839">
                <div data-id="element-1840">
                  <label htmlFor="invoice-number-map" className="block text-sm font-medium text-gray-700" data-id="element-1841">
                    Invoice Number
                  </label>
                  <select id="invoice-number-map" name="invoice-number-map" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={columnMapping.invoiceNumber} onChange={e => handleMappingChange('invoiceNumber', e.target.value)} data-id="element-1842">
                    <option value="" data-id="element-1843">Select column</option>
                    <option value="col1" data-id="element-1844">Column 1 (Invoice Number)</option>
                    <option value="col2" data-id="element-1845">Column 2 (Date)</option>
                    <option value="col3" data-id="element-1846">Column 3 (Amount)</option>
                    <option value="col4" data-id="element-1847">Column 4 (Description)</option>
                  </select>
                </div>
                <div data-id="element-1848">
                  <label htmlFor="invoice-date-map" className="block text-sm font-medium text-gray-700" data-id="element-1849">
                    Invoice Date
                  </label>
                  <select id="invoice-date-map" name="invoice-date-map" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={columnMapping.invoiceDate} onChange={e => handleMappingChange('invoiceDate', e.target.value)} data-id="element-1850">
                    <option value="" data-id="element-1851">Select column</option>
                    <option value="col1" data-id="element-1852">Column 1 (Invoice Number)</option>
                    <option value="col2" data-id="element-1853">Column 2 (Date)</option>
                    <option value="col3" data-id="element-1854">Column 3 (Amount)</option>
                    <option value="col4" data-id="element-1855">Column 4 (Description)</option>
                  </select>
                </div>
                <div data-id="element-1856">
                  <label htmlFor="amount-map" className="block text-sm font-medium text-gray-700" data-id="element-1857">
                    Amount
                  </label>
                  <select id="amount-map" name="amount-map" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={columnMapping.amount} onChange={e => handleMappingChange('amount', e.target.value)} data-id="element-1858">
                    <option value="" data-id="element-1859">Select column</option>
                    <option value="col1" data-id="element-1860">Column 1 (Invoice Number)</option>
                    <option value="col2" data-id="element-1861">Column 2 (Date)</option>
                    <option value="col3" data-id="element-1862">Column 3 (Amount)</option>
                    <option value="col4" data-id="element-1863">Column 4 (Description)</option>
                  </select>
                </div>
                <div data-id="element-1864">
                  <label htmlFor="description-map" className="block text-sm font-medium text-gray-700" data-id="element-1865">
                    Description (Optional)
                  </label>
                  <select id="description-map" name="description-map" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={columnMapping.description} onChange={e => handleMappingChange('description', e.target.value)} data-id="element-1866">
                    <option value="" data-id="element-1867">Select column</option>
                    <option value="col1" data-id="element-1868">Column 1 (Invoice Number)</option>
                    <option value="col2" data-id="element-1869">Column 2 (Date)</option>
                    <option value="col3" data-id="element-1870">Column 3 (Amount)</option>
                    <option value="col4" data-id="element-1871">Column 4 (Description)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white shadow sm:rounded-md" data-id="element-1872">
            <div className="px-4 py-5 sm:p-6" data-id="element-1873">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-1874">
                Preview
              </h4>
              <div className="overflow-x-auto" data-id="element-1875">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1876">
                  <thead className="bg-gray-50" data-id="element-1877">
                    <tr data-id="element-1878">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1879">
                        Column 1
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1880">
                        Column 2
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1881">
                        Column 3
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1882">
                        Column 4
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1883">
                    {previewData.map((row, index) => <tr key={index} data-id="element-1884">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1885">
                          {row.col1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1886">
                          {row.col2}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1887">
                          {row.col3}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1888">
                          {row.col4}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3" data-id="element-1889">
            <button type="button" onClick={() => setCurrentStep('upload')} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1890">
              Back
            </button>
            <button type="button" onClick={validateMapping} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1891">
              Continue
            </button>
          </div>
        </div>}
      {/* Validation Step */}
      {currentStep === 'validation' && <div className="space-y-6" data-id="element-1892">
          {validationErrors.length > 0 ? <div className="rounded-md bg-red-50 p-4" data-id="element-1893">
              <div className="flex" data-id="element-1894">
                <div className="flex-shrink-0" data-id="element-1895">
                  <AlertCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" data-id="element-1896" />
                </div>
                <div className="ml-3" data-id="element-1897">
                  <h3 className="text-sm font-medium text-red-800" data-id="element-1898">
                    There were {validationErrors.length} errors with your
                    submission
                  </h3>
                  <div className="mt-2 text-sm text-red-700" data-id="element-1899">
                    <ul className="list-disc pl-5 space-y-1" data-id="element-1900">
                      {validationErrors.map((error, index) => <li key={index} data-id="element-1901">{error}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            </div> : <div className="rounded-md bg-green-50 p-4" data-id="element-1902">
              <div className="flex" data-id="element-1903">
                <div className="flex-shrink-0" data-id="element-1904">
                  <CheckIcon className="h-5 w-5 text-green-400" aria-hidden="true" data-id="element-1905" />
                </div>
                <div className="ml-3" data-id="element-1906">
                  <h3 className="text-sm font-medium text-green-800" data-id="element-1907">
                    Validation successful
                  </h3>
                  <div className="mt-2 text-sm text-green-700" data-id="element-1908">
                    <p data-id="element-1909">Your statement data is ready for reconciliation.</p>
                  </div>
                </div>
              </div>
            </div>}
          <div className="bg-white shadow sm:rounded-md" data-id="element-1910">
            <div className="px-4 py-5 sm:p-6" data-id="element-1911">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-1912">
                Summary
              </h4>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2" data-id="element-1913">
                <div className="sm:col-span-1" data-id="element-1914">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-1915">Vendor</dt>
                  <dd className="mt-1 text-sm text-gray-900" data-id="element-1916">
                    {mappedData.vendor || 'Not specified'}
                  </dd>
                </div>
                <div className="sm:col-span-1" data-id="element-1917">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-1918">
                    Statement Period
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900" data-id="element-1919">
                    {mappedData.period || 'Not specified'}
                  </dd>
                </div>
                <div className="sm:col-span-1" data-id="element-1920">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-1921">
                    Total Invoices
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900" data-id="element-1922">
                    {previewData.length}
                  </dd>
                </div>
                <div className="sm:col-span-1" data-id="element-1923">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-1924">
                    Mapped Fields
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900" data-id="element-1925">
                    {Object.values(columnMapping).filter(Boolean).length} of 4
                  </dd>
                </div>
              </dl>
            </div>
          </div>
          <div className="flex justify-end space-x-3" data-id="element-1926">
            <button type="button" onClick={() => setCurrentStep('mapping')} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1927">
              Back
            </button>
            <button type="button" onClick={handleComplete} disabled={!validationPassed} className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${validationPassed ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-300 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-1928">
              Start Reconciliation
            </button>
          </div>
        </div>}
    </div>;
}